// 192 -- Match Day Templates manage page. A school's saved hosting-day
// layouts: list, rename, delete. Templates are created from an event's
// schedule ("Save as template") and consumed at event creation ("Start from
// template"); this page is the simple school-scoped admin surface.

import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { LayoutTemplate, Pencil, Trash2 } from "lucide-react";
import type { MatchDayTemplatePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function MatchDayTemplatesPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const location = useLocation();

  const [templates, setTemplates] = useState<MatchDayTemplatePublic[]>([]);
  const [schoolName, setSchoolName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    Promise.all([api.getMatchDayTemplates(schoolId), api.getSchool(schoolId)])
      .then(([res, school]) => {
        setTemplates(res.templates);
        setSchoolName(school.school.name);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load templates"))
      .finally(() => setLoading(false));
  }, [schoolId, location.key]);

  async function handleRename(template: MatchDayTemplatePublic) {
    const name = prompt("Template name", template.name)?.trim();
    if (!name || name === template.name) return;
    try {
      const res = await api.updateMatchDayTemplate(template.id, { name });
      setTemplates((prev) => prev.map((t) => (t.id === template.id ? res.template : t)));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to rename template");
    }
  }

  async function handleDelete(template: MatchDayTemplatePublic) {
    if (!confirm(`Delete the template "${template.name}"? Events already created from it are not affected.`)) return;
    try {
      await api.deleteMatchDayTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete template");
    }
  }

  const state = loading ? "loading" : error ? "error" : templates.length === 0 ? "empty" : "ready";

  return (
    <div className="flex flex-col gap-6" data-page="match-day-templates" data-state={state}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to={`/schools/${schoolId}`} className="hover:underline">{schoolName || "School"}</Link>
            <span>/</span>
            <span>Match day templates</span>
          </div>
          <h1 className="text-2xl font-semibold">Match Day Templates</h1>
          <p className="text-muted-foreground">
            Saved hosting-day layouts: fields, times, and game lengths -- never teams.
          </p>
        </div>
        <Link to={`/schools/${schoolId}/events`} className={buttonVariants({ variant: "outline", size: "sm" })}>
          Events
        </Link>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {loading ? (
        <div className="flex animate-pulse flex-col gap-3">
          <div className="h-24 rounded-lg bg-muted" />
          <div className="h-24 rounded-lg bg-muted" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <LayoutTemplate className="h-8 w-8 text-muted-foreground" aria-hidden />
            <p className="font-medium">No templates yet</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Build a schedule on any event and choose <span className="font-medium text-foreground">Save as template</span>{" "}
              to capture the day&apos;s layout for reuse.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {templates.map((t) => {
            const times = t.slots.map((s) => s.slot_starts_at_local).sort();
            const fields = [...new Set(t.slots.map((s) => s.court_name).filter(Boolean))] as string[];
            return (
              <Card key={t.id} data-item="template" data-template-name={t.name}>
                <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                  <div>
                    <CardTitle className="text-base">{t.name}</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t.slots.length} {t.slots.length === 1 ? "match" : "matches"}
                      {times.length > 0 && <> - {times[0]} to {times[times.length - 1]}</>}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" data-action="rename" onClick={() => handleRename(t)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" data-action="delete" onClick={() => handleDelete(t)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {fields.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      Fields: <span className="text-foreground">{fields.join(", ")}</span>
                    </p>
                  )}
                  <ul className="flex flex-col gap-1" data-region="slots">
                    {t.slots.map((s) => (
                      <li key={s.id} className="flex items-baseline gap-2 text-sm" data-item="slot">
                        <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{s.slot_starts_at_local}</span>
                        <span className="min-w-0 flex-1 truncate">
                          {s.label ?? s.age_group ?? "Match"}
                          {s.age_group && s.label && <span className="text-muted-foreground"> - {s.age_group}</span>}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">{s.court_name ?? "No field"}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
